import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ScanLine, QrCode, Swords, Radio, Check, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import BottomNav from '../components/BottomNav';
import LanguageToggle from '../components/LanguageToggle';
import UserAvatar from '../components/UserAvatar';
import { formatAura } from '../lib/aura';
import { relativeTime } from '../lib/social';

/** Battles hub: pending challenges + live battles + history quick access. */
export default function BattlesPage() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const pendingId = params.get('pending');

  const [challenges, setChallenges] = useState({ incoming: [], outgoing: [] });
  const [live, setLive] = useState([]);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ch, lv, hist] = await Promise.all([
        api.get('/battles/challenges/pending'),
        api.get('/battles/live'),
        user ? api.get(`/battles/history/${user.username}`) : Promise.resolve({ data: { items: [] } }),
      ]);
      setChallenges(ch.data);
      setLive(lv.data.items || []);
      setHistory(hist.data.items || []);
      // If we're waiting for an outgoing challenge that turned into an accepted battle,
      // jump to the battle screen.
      if (pendingId) {
        const still = ch.data.outgoing.find((c) => c.challenge_id === pendingId);
        if (!still) {
          // find matching battle by finding one where user is participant and 'preparing'|'active'
          const active = lv.data.items.find(
            (b) => b.competitor_a?.user_id === user?.user_id || b.competitor_b?.user_id === user?.user_id
          );
          if (active) nav(`/battle/${active.battle_id}`, { replace: true });
        }
      }
    } catch (_e) { /* ignore */ }
  }, [user, pendingId, nav]);

  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, [load]);

  const accept = async (cid) => {
    try {
      const { data } = await api.post(`/battles/challenges/${cid}/accept`);
      toast.success(t('battles.accepted'));
      nav(`/battle/${data.battle_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); }
  };
  const decline = async (cid) => {
    try { await api.post(`/battles/challenges/${cid}/decline`); toast.success(t('battles.declined')); load(); }
    catch { toast.error(t('common.error')); }
  };
  const cancel = async (cid) => {
    try { await api.post(`/battles/challenges/${cid}/cancel`); load(); }
    catch { toast.error(t('common.error')); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <div className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">BATTLES</div>
        <LanguageToggle />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-5">
        {/* QR Actions */}
        <section className="grid grid-cols-2 gap-3">
          <Link to="/me/qr" data-testid="link-my-qr" className="aura-btn-ghost !py-4 flex-col text-sm">
            <QrCode size={22} className="text-aura-cyan" />
            {t('battles.myQr')}
          </Link>
          <Link to="/scan" data-testid="link-scan" className="aura-btn-primary !py-4 flex-col !text-sm">
            <ScanLine size={22} />
            {t('battles.scanOpponent')}
          </Link>
        </section>

        {/* Incoming challenges */}
        {challenges.incoming.length > 0 && (
          <section data-testid="incoming-challenges">
            <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">⚔️ {t('battles.incoming')}</h3>
            <div className="space-y-2">
              {challenges.incoming.map((c) => (
                <div key={c.challenge_id} className="aura-card p-4 flex items-center gap-3" data-testid={`challenge-${c.challenge_id}`}>
                  <UserAvatar user={c.challenger} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-display font-semibold truncate">{c.challenger.name}</div>
                    <div className="text-xs text-aura-textDim truncate">@{c.challenger.username} · <span className="text-aura-cyan font-mono">⚡{formatAura(c.challenger.aura_points)}</span></div>
                  </div>
                  <button onClick={() => accept(c.challenge_id)} data-testid={`accept-${c.challenge_id}`} className="grid place-items-center h-10 w-10 rounded-full bg-aura-gradient text-black shadow-neon-cyan">
                    <Check size={16} />
                  </button>
                  <button onClick={() => decline(c.challenge_id)} data-testid={`decline-${c.challenge_id}`} className="grid place-items-center h-10 w-10 rounded-full border border-red-400/40 text-red-400">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Outgoing challenges */}
        {challenges.outgoing.length > 0 && (
          <section data-testid="outgoing-challenges">
            <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('battles.outgoing')}</h3>
            <div className="space-y-2">
              {challenges.outgoing.map((c) => (
                <div key={c.challenge_id} className="aura-card p-4 flex items-center gap-3">
                  <UserAvatar user={c.opponent} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display truncate">@{c.opponent.username}</div>
                    <div className="text-[11px] text-aura-textDim flex items-center gap-1"><Clock size={10} />{t('battles.waiting')}</div>
                  </div>
                  <button onClick={() => cancel(c.challenge_id)} className="text-xs text-red-400/80">{t('common.cancel')}</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live battles */}
        <section data-testid="live-battles">
          <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2 flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" /> {t('battles.live')}
          </h3>
          {live.length === 0 ? (
            <div className="aura-card p-6 text-center text-sm text-aura-textDim">{t('battles.noLive')}</div>
          ) : (
            <div className="space-y-2">
              {live.map((b) => (
                <Link key={b.battle_id} to={`/battle/${b.battle_id}`} data-testid={`live-${b.battle_id}`} className="aura-card p-4 flex items-center gap-3 hover:border-aura-cyan/40 transition">
                  <UserAvatar user={b.competitor_a} size="sm" />
                  <div className="flex-1 min-w-0 text-center">
                    <div className="text-[10px] text-aura-textDim">@{b.competitor_a.username} <span className="text-aura-cyan mx-1">vs</span> @{b.competitor_b.username}</div>
                    <div className={`text-[11px] font-display font-bold tracking-widest mt-0.5 ${b.status === 'voting' ? 'text-aura-yellow' : 'text-aura-cyan'}`}>
                      {b.status === 'voting' ? t('battles.voteNow') : b.status === 'preparing' ? t('battles.preparing') : t('battles.watch')}
                    </div>
                  </div>
                  <UserAvatar user={b.competitor_b} size="sm" />
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* History */}
        {user && history.length > 0 && (
          <section data-testid="battle-history">
            <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('battles.history')}</h3>
            <div className="space-y-2">
              {history.slice(0, 10).map((b) => {
                const iAmA = b.competitor_a.user_id === user.user_id;
                const opp = iAmA ? b.competitor_b : b.competitor_a;
                let result = 'DRAW';
                let color = '#A0A0B0';
                if (b.winner_id === user.user_id) { result = 'WIN'; color = '#00FFA3'; }
                else if (b.winner_id && b.winner_id !== user.user_id) { result = 'LOSS'; color = '#FF2E9F'; }
                return (
                  <Link key={b.battle_id} to={`/battle/${b.battle_id}`} className="aura-card p-3 flex items-center gap-3">
                    <UserAvatar user={opp} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-display truncate">vs @{opp.username}</div>
                      <div className="text-[11px] text-aura-textDim">{relativeTime(b.created_at, locale)} · {b.total_votes} votes</div>
                    </div>
                    <div className="text-xs font-display font-bold tracking-widest" style={{ color }}>{result}</div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
